/*
 * ____   ____    .__   __
 * \   \ /   /___ |  |_/  |______      ____ ___.__.
 *  \   Y   /  _ \|  |\   __  \_ \   / ___<   |  |
 *   \     (  <_> )  |_|  |  / __ \_/ /_/  >___  |
 *    \___/ \____/|____/__| (____  /\___  // ____|
 *                               \//_____/ \/
 *
 * Copyright (c) 2017-2025 Voltagy. All rights reserved.
 * https://voltagy.com
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the proprietary and confidential property of Voltagy.
 * Unauthorized copying, transfer, or use of this file, via any medium, is
 * strictly prohibited without the express written permission of Voltagy.
 *
 * The receipt or possession of this source code and/or related information
 * does not convey or imply any right to use it, reproduce it, disclose its
 * contents, or to manufacture, use, or sell anything that it may describe.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS"
 * AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE
 * IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE
 * ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT HOLDER OR CONTRIBUTORS BE
 * LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR
 * CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF
 * SUBSTITUTE GOODS OR SERVICES; LOSS OF USE, DATA, OR PROFITS; OR BUSINESS
 * INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN
 * CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE)
 * ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 *
 */

import { api as VoltagyAPI } from "../../../services/VoltagyAPI";

export const updateUserEmail = async (userId, newEmail) => {
  try {
    if (!newEmail) {
      throw new Error("New email is required");
    }
    const response = await VoltagyAPI.put(`/users/${userId}`, {
      email: newEmail,
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else if (error.response.status === 409) {
        throw new Error("Email address is already in use");
      } else {
        throw new Error(`Server error: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error(
        "A temporary error has been encountered. Please try again later."
      );
    }
  }
};

export const updateUserPassword = async (
  userId,
  currentPassword,
  newPassword
) => {
  try {
    if (!currentPassword || !newPassword) {
      throw new Error("Current password and new password are required");
    }
    const response = await VoltagyAPI.put(`/users/${userId}`, {
      currentPassword,
      newPassword,
    });
    return response.data;
  } catch (error) {
    if (error.response) {
      if (error.response.data && error.response.data.error) {
        throw new Error(error.response.data.error);
      } else if (error.response.status === 401) {
        throw new Error("Current password is incorrect");
      } else {
        throw new Error(`Server error: ${error.response.status}`);
      }
    } else if (error.request) {
      throw new Error("No response received from server");
    } else {
      throw new Error(
        "A temporary error has been encountered. Please try again later."
      );
    }
  }
};

export const updateUser = async (userId, userData) => {
  try {
    const updatedFields = {};
    if (userData.email !== undefined && userData.email !== null) {
      updatedFields.email = userData.email;
    }
    if (userData.username !== undefined && userData.username !== null) {
      updatedFields.username = userData.username;
    }
    if (userData.newPassword !== undefined && userData.newPassword !== null) {
      updatedFields.newPassword = userData.newPassword;
    }
    if (userData.enabled !== undefined) {
      updatedFields.enabled = userData.enabled;
    }
    if (userData.roleId !== undefined) {
      updatedFields.roleId = userData.roleId;
    }
    if (userData.sso_only !== undefined) {
      updatedFields.sso_only = userData.sso_only;
    }
    if (userData.properties !== undefined) {
      updatedFields.properties = userData.properties;
    }

    if (Object.keys(updatedFields).length === 0) {
      throw new Error("No valid fields to update");
    }

    const response = await VoltagyAPI.put(`/users/${userId}`, updatedFields);
    return response.data;
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    const response = await VoltagyAPI.get("/users");
    return response.data;
  } catch (error) {
    console.error("Error fetching all users:", error);
    throw error;
  }
};

export const getAllRoles = async () => {
  try {
    const response = await VoltagyAPI.get("/users/roles");
    return response.data;
  } catch (error) {
    console.error("Error fetching all roles:", error);
    throw error;
  }
};

export const getAllPermissions = async () => {
  try {
    const response = await VoltagyAPI.get("/users/permissions");
    return response.data;
  } catch (error) {
    console.error("Error fetching all permissions:", error);
    throw error;
  }
};

export const addUser = async (userData) => {
  try {
    const response = await VoltagyAPI.post("/users", userData);
    return response.data;
  } catch (error) {
    console.error("Error adding new user:", error);
    throw error;
  }
};

export const addPermissionToRole = async (roleId, permissionId) => {
  try {
    const response = await VoltagyAPI.post(
      `/users/roles/${roleId}/permissions`,
      {
        permissionId,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding permission to role:", error);
    throw error;
  }
};

export const removePermissionFromRole = async (roleId, permissionId) => {
  try {
    const response = await VoltagyAPI.delete(
      `/users/roles/${roleId}/permissions/${permissionId}`
    );
    return response.data;
  } catch (error) {
    console.error("Error removing permission from role:", error);
    throw error;
  }
};

export const getUsersInRole = async (roleId) => {
  try {
    const response = await VoltagyAPI.get(`/users/roles/${roleId}/users`);
    return response.data;
  } catch (error) {
    console.error("Error fetching users in role:", error);
    throw error;
  }
};

export const changeUserRole = async (userId, newRoleId) => {
  try {
    const response = await VoltagyAPI.put(`/users/${userId}/role`, {
      roleId: newRoleId,
    });
    return response.data;
  } catch (error) {
    console.error("Error changing user role:", error);
    throw error;
  }
};

export const addRole = async (roleName) => {
  try {
    const response = await VoltagyAPI.post("/users/roles", {
      name: roleName,
    });
    return response.data;
  } catch (error) {
    console.error("Error adding new role:", error);
    throw error;
  }
};

export const updateRole = async (roleId, roleData) => {
  try {
    if (!roleData.name) {
      throw new Error("Role name is required");
    }
    const response = await VoltagyAPI.put(`/users/roles/${roleId}`, roleData);
    return response.data;
  } catch (error) {
    console.error("Error updating role:", error);
    throw error;
  }
};

export const deleteRole = async (roleId) => {
  try {
    await VoltagyAPI.delete(`/users/roles/${roleId}`);
  } catch (error) {
    console.error("Error deleting role:", error);
    throw error;
  }
};

export const getDefaultRole = async () => {
  try {
    const response = await VoltagyAPI.get("/users/default-role");
    return response.data;
  } catch (error) {
    console.error("Error fetching default role:", error);
    throw error;
  }
};

export const resetUserPassword = async (userId) => {
  try {
    const response = await VoltagyAPI.post(`/users/${userId}/reset-password`);
    const { tempPassword } = response.data;
    return tempPassword;
  } catch (error) {
    console.error("Error resetting user password:", error);
    throw error;
  }
};

export const sendPasswordResetEmail = async (
  to,
  username,
  tempPassword,
  appUrl
) => {
  try {
    const subject = "Voltagy GridCritical - Password Reset";
    const body = `
      <h1>Password Reset</h1>
      <p>Hello! Your GridCritical password has been reset. Please use the following information to log in:</p>
      <p><strong>App URL:</strong> ${appUrl}</p>
      <p><strong>Username:</strong> ${username}</p>
      <p><strong>Temporary Password:</strong> ${tempPassword}</p>
      <p>Please change your password after logging in.</p>
    `;

    const response = await VoltagyAPI.post("/email/send", {
      to,
      subject,
      body,
      isHtml: true,
      isPasswordReset: true,
    });

    return response.data;
  } catch (error) {
    console.error("Error sending password reset email:", error);
    throw error;
  }
};

export const getUserPreferences = async (userId) => {
  try {
    const response = await VoltagyAPI.get(`/users/${userId}/preferences`);
    return response.data;
  } catch (error) {
    console.error("Error fetching user preferences:", error);
    throw error;
  }
};

export const updateUserPreferences = async (userId, newPreferences) => {
  try {
    const response = await VoltagyAPI.put(
      `/users/${userId}/preferences`,
      newPreferences
    );
    return response.data;
  } catch (error) {
    console.error("Error changing user preferences:", error);
    throw error;
  }
};

export const deleteUserPreferences = async (userId) => {
  try {
    await VoltagyAPI.delete(`/users/${userId}/preferences`);
  } catch (error) {
    console.error("Error deleting user preferences:", error);
    throw error;
  }
};

export const bulkUpdateUsers = async (userIds, updateData) => {
  try {
    const apiUpdateData = { ...updateData };

    if (apiUpdateData.authType) {
      if (apiUpdateData.authType === "sso") {
        apiUpdateData.sso_only = true;
      }

      delete apiUpdateData.authType;
    }

    const response = await VoltagyAPI.post("/users/bulk-update", {
      userIds,
      ...apiUpdateData,
    });
    return response.data;
  } catch (error) {
    console.error("Error performing bulk update on users:", error);
    throw error;
  }
};

export const disableUser = async (userId) => {
  try {
    await VoltagyAPI.delete(`/users/${userId}`);
    return true;
  } catch (error) {
    console.error("Error disabling user:", error);
    throw error;
  }
};

export const permanentlyDeleteUser = async (userId) => {
  try {
    await VoltagyAPI.delete(`/users/${userId}?permanent=true`);
    return true;
  } catch (error) {
    console.error("Error permanently deleting user:", error);
    throw error;
  }
};

export const bulkPermanentlyDeleteUsers = async (userIds) => {
  try {
    const response = await VoltagyAPI.post("/users/bulk-permanent-delete", {
      userIds,
    });
    return response.data;
  } catch (error) {
    console.error("Error permanently deleting users in bulk:", error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    await VoltagyAPI.delete(`/users/${userId}`);
    return true;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

export const exportUsersToCSV = async (userIds = null) => {
  try {
    const endpoint = userIds ? "/users/export" : "/users/export-all";
    const response = await VoltagyAPI.post(
      endpoint,
      userIds ? { userIds } : {},
      {
        responseType: "blob",
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `gridcritical-users-${new Date().toISOString().slice(0, 10)}.csv`
    );
    document.body.appendChild(link);
    link.click();

    window.URL.revokeObjectURL(url);
    document.body.removeChild(link);

    return true;
  } catch (error) {
    console.error("Error exporting users to CSV:", error);
    throw error;
  }
};

export const importUsersFromCSV = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await VoltagyAPI.post("/users/import", formData);

    return response.data;
  } catch (error) {
    console.error("Error importing users from CSV:", error);
    throw error;
  }
};
